import React, { useState } from 'react'
import { Autocomplete, Box, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import FormField from '../../../../../../../../component/Formfield';
import BtnPrimary from "../../../../../../../../component/PrimaryButton";
import MultiSectionClock from '../../../../../../../../component/MultiSectionClock';
import { FormSection, FieldLabel, SwitchRow, WizardActions } from '../../../../../../../../component/wizard';
import { getOrgShifts } from '../../../../../../../../services/globalData';
import { tokens } from '../../../../../../../../theme';

const TardinessForm = ({ loadNextPage, tardinessForm, setTardinessForm }) => {

  const handleChange = (field, value) => {
    setTardinessForm(prevState => ({ ...prevState, [field]: value }));
  };
  const shiftOptions = (getOrgShifts() || []).map((shift) => ({ value: shift?._id, name: shift?.name }));
  // rehydrate the multi-select from the persisted ids when navigating back into the form
  const [selectedShifts, setSelectedShifts] = useState(
    shiftOptions.filter((opt) => (tardinessForm?.shifts || []).includes(opt.value))
  );

  const handleShiftChange = (event, newValue) => {
    setSelectedShifts(newValue);
    handleChange('shifts', newValue.map((shift) => shift?.value));
  };

  const isLowPunctuality = tardinessForm?.tardinessType === "low-punctuality";
  const isPenalised = tardinessForm?.tardinessPenalty !== "none";
  const allowed = Number(tardinessForm?.noOfPermissibleLateArrivals) || 0;

  return (
    <Box>
      <FormSection title="Tardiness" description="Penalize employees who exceed the allowed number / hours of late arrival.">
        <SwitchRow
          title="Enable tardiness tracking"
          description="Track late arrivals or low punctuality against this policy."
          checked={tardinessForm?.enableTardiness}
          onChange={(e) => handleChange('enableTardiness', e.target.checked)}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "1.25rem", mt: "1.25rem" }}>
          <Box>
            <FieldLabel>Tardiness type</FieldLabel>
            <FormField
              type="select" noMargin height="2.75rem"
              options={[{ name: "Late Login / Clock In", value: "late-login" }, { name: "Low Punctuality", value: "low-punctuality" }]}
              value={tardinessForm?.tardinessType}
              onChange={(e) => handleChange('tardinessType', e.target.value)}
            />
          </Box>
          <Box>
            <FieldLabel>Scheduled shifts</FieldLabel>
            <Autocomplete
              multiple options={shiftOptions} getOptionLabel={(o) => o?.name || ""} size="small"
              value={selectedShifts} onChange={handleShiftChange}
              sx={{ '& .MuiInputBase-root': { borderRadius: '10px', minHeight: "2.75rem" } }}
              renderInput={(params) => <TextField {...params} placeholder="All shifts" />}
            />
          </Box>
        </Box>
      </FormSection>

      <FormSection
        title={isLowPunctuality ? "Punctuality threshold" : "Grace period"}
        description={isLowPunctuality
          ? "Below this punctuality rate the employee is flagged as low-punctuality."
          : "Arrivals beyond this buffer are counted as late."}
      >
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "0.75rem", flexWrap: "wrap" }}>
          {isLowPunctuality ? (
            <>
              <Box>
                <FieldLabel>Minimum punctuality (%)</FieldLabel>
                <FormField type="text" width="7rem" height="2.75rem" noMargin
                  value={tardinessForm?.minimumPunctuality}
                  onChange={(e) => handleChange('minimumPunctuality', e.target.value)} />
              </Box>
              <Box>
                <FieldLabel>Per</FieldLabel>
                <FormField type="select" width="11rem" height="2.75rem" noMargin
                  options={[{ name: "Week", value: "weekly" }, { name: "Month", value: "monthly" }, { name: "Year", value: "yearly" }]}
                  value={tardinessForm?.punctualityTimePeriod}
                  onChange={(e) => handleChange('punctualityTimePeriod', e.target.value)} />
              </Box>
            </>
          ) : (
            <Box>
              <FieldLabel>Grace period</FieldLabel>
              <MultiSectionClock
                initialHours={Math.floor(tardinessForm?.tardinessThreshold / 60) || 0}
                initialMinutes={Math.floor(tardinessForm?.tardinessThreshold % 60) || 0}
                onChangeTime={(value) => handleChange('tardinessThreshold', value)}
              />
            </Box>
          )}
          <Box>
            <FieldLabel>Permissible late arrivals</FieldLabel>
            <FormField type="text" width="7rem" height="2.75rem" noMargin
              value={tardinessForm?.noOfPermissibleLateArrivals}
              onChange={(e) => handleChange('noOfPermissibleLateArrivals', e.target.value)} />
          </Box>
        </Box>
      </FormSection>

      <FormSection title="Penalty on breach" description="What happens when an employee exceeds the allowed late arrivals.">
        <SwitchRow
          title="Apply a penalty"
          description="Off = late arrivals are tracked but not penalised."
          checked={isPenalised}
          onChange={(e) => handleChange('tardinessPenalty', e.target.checked ? "deduct" : "none")}
        />

        {isPenalised && (
          <Box sx={{ mt: "1.25rem" }}>
            <FieldLabel>How are penalties charged?</FieldLabel>
            <RadioGroup
              value={tardinessForm?.tardinessPenalty}
              onChange={(e) => handleChange('tardinessPenalty', e.target.value)}
              sx={{ flexDirection: "row", gap: "1.5rem", mb: "1.25rem" }}
            >
              <FormControlLabel value="deduct" control={<Radio />} label={
                <Box>
                  Loss of Pay
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Dock pay for each incident.</Box>
                </Box>
              } />
              <FormControlLabel value="leave" control={<Radio />} label={
                <Box>
                  Paid Leave
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Deduct from the employee's paid-leave balance.</Box>
                </Box>
              } />
            </RadioGroup>

            <Box sx={{ fontSize: "0.875rem", color: tokens.text, mb: "0.5rem" }}>
              After <b>{allowed || "the allowed"}</b> late arrival{allowed === 1 ? "" : "s"}, how is the employee penalised?
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", color: tokens.textMuted, fontSize: "0.875rem" }}>
              {tardinessForm?.tardinessPenalty === "deduct" ? (
                <>
                  <FormField type="text" width="6rem" height="2.5rem" noMargin
                    value={tardinessForm.noOfDaysOfPayLossForTardiness}
                    onChange={(e) => handleChange('noOfDaysOfPayLossForTardiness', e.target.value)} />
                  day(s) loss of pay per incident.
                </>
              ) : (
                <>
                  <FormField type="text" width="6rem" height="2.5rem" noMargin
                    value={tardinessForm.noOfPaidLeaveDeductForTardiness}
                    onChange={(e) => handleChange('noOfPaidLeaveDeductForTardiness', e.target.value)} />
                  day(s) of paid leave per incident.
                </>
              )}
            </Box>
          </Box>
        )}
      </FormSection>

      <WizardActions>
        <BtnPrimary sx={{ borderRadius: "8px" }} onClick={() => loadNextPage("chooseCategory")}>
          Save
        </BtnPrimary>
      </WizardActions>
    </Box>
  )
}

export default TardinessForm
